import React, { useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import SpiMasker from "../../../utilities/spiMasker";
import { arrayMove } from "../../../utilities/utils";
import { CardHeading, Divider } from "../profile/index.style";
import { useMutation } from "../../../GraphQL/helpers";
import { usePerfil } from "../../../contexts/pages/perfilContext";
import { Row, Column } from "../../global/grid";
import AdaHelper from "../../global/adaHelper";
import Panel, { PanelHeader, PanelHeaderActions, PanelBody } from "../../global/panel";
import ButtonLink from "../../global/buttonLink";
import Badge from "../../global/badge";
import EmptyBox from "../../global/emptyBox";
import PhoneModal from "../phoneModal";
import DeletePhoneModal from "../deletePhoneModal";
import GET_PROFILE from "../../../GraphQL/queries/fetchProfile";
import { MAKE_PRIMARY_PHONE } from "../../../GraphQL/mutations/phone";
import errorHandler from "../../../utilities/errorHandler";

const Phones = (props) => {
    const { t } = useTranslation("components.profile.phones");
    const [updatePhone] = useMutation(MAKE_PRIMARY_PHONE);
    const { setError } = usePerfil();
    const [isNewPhone, setNewPhone] = useState(false);
    const [selectedPhone, setEditPhone] = useState({});
    const [toBeDeletedPhone, setDeletedPhone] = useState({});
    const phoneModal = useRef(null);
    const deletePhoneModal = useRef(null);
    const phones = useMemo(() => {
        if (props.phones) {
            const mainPhoneIndex = props.phones.findIndex(p => p.principal);

            if (mainPhoneIndex > -1) {
                return arrayMove(props.phones, mainPhoneIndex, 0);
            }

            return props.phones;
        }

        return [];
    }, [props.phones]);

    const addPhone = (e) => {
        setNewPhone(true);
        setEditPhone({ principal: !props.phones.length });
        phoneModal?.current && phoneModal.current.open();
    };

    const editPhone = (phone) => {
        setNewPhone(false);
        setEditPhone(phone);
        phoneModal?.current && phoneModal.current.open();
    };

    const deletePhone = (phone) => {
        setDeletedPhone(phone);
        deletePhoneModal?.current && deletePhoneModal.current.open();
    };

    const makePrimary = async (phone) => {
        try {
            const { data: resultData } = await updatePhone({
                variables: {
                    id: phone.id,
                    principal: true,
                },
                optimisticResponse: {
                    editarTelefono: {
                        telefono: {
                            id: phone.id,
                            principal: true,
                            __typename: "Telefono",
                        },
                        errores: null,
                    }
                },
                update(cache, { data: { editarTelefono } }) {
                    if (editarTelefono?.telefono?.principal ?? false) {
                        const { perfil } = cache.readQuery({ query: GET_PROFILE });
                        const currentMainPhone = perfil.telefonos.find(t => t.principal && t.id !== editarTelefono.telefono.id);

                        if (currentMainPhone.id !== editarTelefono.telefono.id) {
                            cache.modify({
                                id: cache.identify(currentMainPhone),
                                fields: { principal: () => false },
                            });
                        }
                    }
                },
            });
            const { errores } = resultData.editarTelefono;

            if (errores) {
                setError({
                    title: t("errors.makePrimary.default.title"),
                    message: errores[0].mensaje,
                });
            }
        } catch (error) {
            errorHandler(error, () => {
                setError(t("errors.makePrimary.default", { returnObjects: true }));
            });
        }
    };

    const onPhoneModalClose = () => {
        setNewPhone(false);
    };

    return (
        <>
            <Panel variant="shadowed">
                <PanelHeader>
                    <CardHeading>{t("title")}</CardHeading>
                    <PanelHeaderActions>
                        <ButtonLink onClick={addPhone}>
                            <FontAwesomeIcon icon={faPlus} />
                            <AdaHelper>{t("addPhoneAda")}</AdaHelper>
                        </ButtonLink>
                    </PanelHeaderActions>
                </PanelHeader>
                <PanelBody>
                    {!phones.length && <EmptyBox>
                        <ButtonLink onClick={addPhone}>{t("add")}</ButtonLink>
                    </EmptyBox>}

                    {phones.map((p) => (
                        <Row pb={{ _: 4 }} key={p.id}>
                            <Column lg="7">
                                <div>{SpiMasker.phone(p.numero)}</div>
                                {p.principal && <Badge colors="primaryWithBg" mr={2}>{t("primary")}</Badge>}
                                {!p.principal && <ButtonLink onClick={() => makePrimary(p)}>{t("makePrimary")}</ButtonLink>}
                            </Column>
                            {!p.principal && (
                                <Column lg="5" textAlign={{ lg: "right" }}>
                                    <ButtonLink onClick={() => editPhone(p)}>{t("edit")}</ButtonLink>
                                    <Divider>|</Divider>
                                    <ButtonLink onClick={() => deletePhone(p)}> {t("delete")}</ButtonLink>
                                </Column>
                            )}
                        </Row>
                    ))}
                </PanelBody>
            </Panel>
            <PhoneModal onClosed={onPhoneModalClose} isNewPhone={isNewPhone}
                        phone={selectedPhone} innerRef={phoneModal}/>
            <DeletePhoneModal phone={toBeDeletedPhone} innerRef={deletePhoneModal}/>
        </>
    );
};

export default Phones;
